import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from 'components/system/layouts/Layout';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ScreenContext from 'Contexts/Common/ScreenContext';
import COMMON from 'constants/common';
import redirectLinks from 'constants/redirectLinks';
import ReturnButton from 'components/system/atoms/buttons/ReturnButton';
import CLSFCN from 'constants/classification';
import InfoDialog from 'components/system/organisms/dialogs/InfoDialog';
import { useAppDispatch } from 'redux/hooks';
import PrevNextButton from 'components/system/atoms/buttons/PrevNextButton';
import { animateScroll as scroll } from 'react-scroll/modules';
import { NormalSelectBox } from 'components/system/organisms';
import { getClsfcnByCategory } from 'utils/common.helper';
import DecisionButton from 'components/machineConf/atoms/DecisionButton';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import { MachineStationType, StationResponseType } from 'types/maintenance/StationType';
import ConfirmDialog from 'components/system/organisms/dialogs/ConfirmDialog';
import MsgHelper from 'utils/message.helper';
import MSG from 'constants/messages';
import { NewGiftValueType } from 'components/maintenance/gift/NewGift';
import TooltipComponent from 'components/system/organisms/dialogs/TooltipComponent';
import MachineStationTable from 'components/data/organisms/tables/MachineStationTable';

/* ************ Context ************ */
const ScreenContextValue = {
  title: '景品登録先一覧',
  screenId: COMMON.SCREEN_ID.REGISTER_GIFT_DESTINATION_LIST,
  currentMenu: COMMON.MENU.MAINTENANCE,
};

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  content: {
    height: '598px',
    width: '1140px',
    padding: '0 16px',
  },
  topArea: {
    display: 'flex',
    justifyContent: 'flex-start',
    '& .returnButton': {
      padding: '10px 0px 0px 0px',
      width: '224px',
    },
  },
  decision: {
    textAlign: 'right',
  },
  buttonSize: {
    fontSize: '28px',
  },
  boxTextRight: {
    display: 'inline-flex',
    margin: '20px 0px 0px 15px',
  },
  giftNameType: {
    width: '718px',
    marginRight: '12px',
  },
  giftPriceType: {
    width: '138px',
  },
  mainTable: {
    marginTop: '24px',
  },
  previousBtn: {
    paddingBottom: '10px',
    position: 'inherit',
    textAlign: 'center',
    width: '100%',
    height: '29px',
  },
  nextBtn: {
    paddingTop: '5px',
    textAlign: 'center',
    height: '29px',
  },
  boxTextRightContent: {
    display: 'inline-flex',
    alignItems: 'flex-end',
  },
  textUnderline: {
    fontSize: 24,
    borderBottom: '2px solid #707070',
    overflow: 'hidden',
    '& .noWrap': {
      whiteSpace: 'nowrap',
    },
  },
  selectedStationTable: {
    height: '381px',
    '& #dataStationTable': {
      height: '381px',
    },
  },
  boxTextRightSort: {
    position: 'absolute',
    left: '928px',
    top: '157px',
  },
  contentText: { fontSize: '24px' },
}));

type SubmitEntryGiftResponseType = {
  result: boolean;
};

// messages
const messageConfirm: string = MsgHelper.messageFormat(MSG.INF232);
const messageSuccess: string = MsgHelper.messageFormat(MSG.INF308, '景品', '登録景品一覧');

const TABLE_ID = 'dataStationTable';
const MAX_DISPLAY_BAR = 6;

const RegisterGiftDestinationList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const [stationList, setStationList] = React.useState<Array<Array<StationResponseType>>>([]);
  const [giftInfo, setGiftInfo] = React.useState<NewGiftValueType>({ giftName: '', giftPrice: '' });
  const sortDataSource = getClsfcnByCategory(CLSFCN.SORT_STATION_LIST.CATEGORY_CD);
  const [sort, setSort] = React.useState<string>(CLSFCN.SORT_STATION_LIST.STATION_NAME_ASC);
  const [openInfoDialog, setOpenInfoDialog] = React.useState(false);
  const [openSaveConfirmDlg, setOpenSaveConfirmDlg] = React.useState(false);
  const [numberStation, setNumberStation] = React.useState(0);

  const handleClickGroupConfTop = () => {
    navigate(redirectLinks.GIFT_DETAIL, {
      state: { ...giftInfo },
    });
  };

  /* ************ API ************ */
  const getStationDestList = async (sortType: string) => {
    const http = new HttpConnection({ dispatch });
    const data = await http.get<Array<Array<StationResponseType>>>(REST_API.MAINTENANCE.GET_STATION_LIST, {
      sort: sortType,
    });
    setStationList(data);
    let count = 0;
    data.forEach((items) => {
      count += items.length;
    });
    setNumberStation(count);
  };

  const submitEntryGift = async () => {
    const http = new HttpConnection({ dispatch });
    const arrStations: MachineStationType[] = [];

    for (let i = 0; i < stationList.length; i += 1) {
      for (let j = 0; j < stationList[i].length; j += 1) {
        const station = stationList[i][j];
        if (station.selected) {
          arrStations.push({
            stationId: station.stationId,
            boardSerial: station.boardSerial,
            boardStation: station.boardStation,
            machineType: station.machineType,
          });
        }
      }
    }

    const data = await http.post<SubmitEntryGiftResponseType>(REST_API.MAINTENANCE.POST_ENTRY_GIFT, {
      giftName: giftInfo.giftName,
      giftCost: giftInfo.giftPrice,
      stationList: arrStations,
    });
    if (data.result) {
      setOpenInfoDialog(true);
    }
  };

  useEffect(() => {
    const giftInfoParam: NewGiftValueType =
      location.state !== undefined && location.state !== null ? (location.state as NewGiftValueType) : giftInfo;
    setGiftInfo({ ...giftInfoParam });
    getStationDestList(sort).then();
  }, []);

  // sort change
  const handleChangeSort = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const sortType = ev.target.value;
    setSort(sortType);
    getStationDestList(sortType).then();
  };

  const handleClickPrev = () => {
    scroll.scrollMore(-58, {
      duration: 50,
      delay: 0,
      containerId: TABLE_ID,
    });
  };

  const handleClickNext = () => {
    scroll.scrollMore(58, {
      duration: 50,
      delay: 0,
      containerId: TABLE_ID,
    });
  };

  const handleOkSaveConfirmDlg = () => {
    setOpenSaveConfirmDlg(false);
    submitEntryGift().then();
  };

  const handleChangeSelectStation = useCallback(
    (boardSerial: string, stationId: number | null) => {
      let found = false;
      for (let i = 0; i < stationList.length; i += 1) {
        for (let j = 0; j < stationList[i].length; j += 1) {
          if (stationList[i][j].boardSerial === boardSerial && stationList[i][j].stationId === stationId) {
            stationList[i][j].selected = !stationList[i][j].selected;
            found = true;
            break;
          }
        }
        if (found) {
          break;
        }
      }
      setStationList([...stationList]);
    },
    [stationList],
  );

  const handleClickDecisionButton = () => {
    setOpenSaveConfirmDlg(true);
  };

  const handleSuccessButton = () => {
    navigate(redirectLinks.MAINT_GIFT_LIST);
  };

  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <Layout>
        <div className={classes.content}>
          <div className={classes.topArea}>
            <div className="returnButton">
              <ReturnButton onClick={handleClickGroupConfTop}>景品詳細</ReturnButton>
            </div>
            <div className={classes.boxTextRight}>
              <div className={classes.giftNameType}>
                <div className={classes.textUnderline}>
                  <TooltipComponent tooltipText={giftInfo.giftName} displayWord={`景品名称 : ${giftInfo.giftName}`} />
                </div>
              </div>
              <div className={classes.giftPriceType}>
                <div className={classes.textUnderline}>
                  <div className="noWrap">{`原価 : ${giftInfo.giftPrice}`}</div>
                </div>
              </div>
            </div>
          </div>
          <div className={classes.mainTable}>
            <Grid className={classes.previousBtn}>
              {numberStation > 0 && <PrevNextButton isUp className="prevScroll" onClick={handleClickPrev} />}
            </Grid>
            <div className={classes.boxTextRightSort}>
              <div className={classes.boxTextRightContent}>
                <NormalSelectBox
                  name="sort"
                  dataSource={sortDataSource}
                  value={sort}
                  onChange={handleChangeSort}
                  label="並べ替え"
                />
              </div>
            </div>
            <div className={classes.selectedStationTable}>
              <MachineStationTable
                tableId={TABLE_ID}
                stationList={stationList}
                handleSelectedItemChange={handleChangeSelectStation}
              />
            </div>
            <Grid className={classes.nextBtn}>
              {MAX_DISPLAY_BAR <= numberStation && <PrevNextButton className="nextScroll" onClick={handleClickNext} />}
            </Grid>
          </div>
          <Grid className={classes.decision}>
            <DecisionButton onClick={handleClickDecisionButton}>
              <span className={classes.buttonSize}>登録</span>
            </DecisionButton>
          </Grid>
        </div>
        <ConfirmDialog
          open={openSaveConfirmDlg}
          msg={messageConfirm}
          onOk={handleOkSaveConfirmDlg}
          onCancel={() => setOpenSaveConfirmDlg(false)}
          addClass={{
            contentText: classes.contentText,
          }}
        />
        <InfoDialog open={openInfoDialog} msg={messageSuccess} closeFunc={handleSuccessButton} />
      </Layout>
    </ScreenContext.Provider>
  );
};

export default RegisterGiftDestinationList;
